import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  CircularProgress,
  Grid,
  InputAdornment,
  Link,
  OutlinedInput,
  Typography
} from '@mui/material'
import { isEmpty, slice } from 'lodash'
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'

import { MButton, Pagination } from 'components'
import { ADMIN_CONTACT_EMAIL } from 'config'
import { color } from 'config/color'
import { useAuth } from 'context'
import { isAuthorized } from 'utils'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
import { AddCampaignCard } from './AddCampaignCard'
import { CampaignListCard } from './CampaignListCard'

interface ICampaignList {
  setActiveTab: Dispatch<SetStateAction<number>>
}

export const CampaignList = (props: ICampaignList): JSX.Element => {
  const isMobile = useMobileBreakpoints()
  const [loading, setLoading] = useState<boolean>(false)
  const [origAllCampaigns, setOrigAllCampaigns] = useState([])
  const [filteredCampaigns, setFilteredCampaigns] = useState([])
  const [campaigns, setCampaigns] = useState(filteredCampaigns)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [pageCount, setPageCount] = useState(0)
  const { getCampaigns, handleLogOut } = useAuth()

  const handlePagination = (_e: any, value: any): void => {
    setPage(value)
  }

  const handleSearch = (e: any): void => {
    setSearch(e.target.value)
  }

  const initCampaigns = async (): Promise<void> => {
    try {
      setLoading(true)
      const result = await getCampaigns()
      setFilteredCampaigns(result.campaign)
      setOrigAllCampaigns(result.campaign)
    } catch (error) {
      if (!isAuthorized(error)) await handleLogOut()

      setFilteredCampaigns([])
      setOrigAllCampaigns([])
    }
    setPage(1)
    setLoading(false)
  }

  useEffect(() => {
    void initCampaigns()
  }, [])

  useEffect(() => {
    if (isEmpty(search)) {
      setFilteredCampaigns(origAllCampaigns)
    } else {
      const filteredCampaigns = origAllCampaigns.filter((item: any) =>
        item.name.toLowerCase().includes(search.toLowerCase())
      )
      setFilteredCampaigns(filteredCampaigns)
      setPageCount(Math.ceil(filteredCampaigns.length / 9))
      setPage(1)
    }
  }, [search])

  useEffect(() => {
    setCampaigns(slice(filteredCampaigns, (page - 1) * 9, page * 10 - page))
  }, [filteredCampaigns, page])

  useEffect(() => {
    setPageCount(Math.ceil(filteredCampaigns.length / 9))
  }, [filteredCampaigns, search])

  return (
    <Box bgcolor={color.white}>
      <Box
        display="flex"
        p={3}
        gap={1}
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        borderBottom={`solid 1px ${color.secondary.dark[200]}`}
      >
        <Typography color={color.grey[900]} fontWeight={400} fontSize={24}>
          Campaign List
        </Typography>

        <MButton
          variant="contained"
          type="button"
          rounded="true"
          texttransform="none"
          startIcon={<AddIcon />}
          onClick={() => {
            props.setActiveTab(1)
          }}
        >
          Create Campaign
        </MButton>
      </Box>

      <Box display="flex" flexDirection="column" p={3} mb={3}>
        {(campaigns.length > 0 || !isEmpty(search)) && (
          <Box display="flex" justifyContent="space-between">
            <OutlinedInput
              name="search"
              onChange={handleSearch}
              placeholder="Search Campaign"
              endAdornment={
                <InputAdornment position="end">
                  <SearchIcon sx={{ color: color.primary[600] }} />
                </InputAdornment>
              }
              inputProps={{
                style: { color: color.grey[700] }
              }}
            />
          </Box>
        )}

        {loading
          ? (
            <Box p={2} display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          )
          : (
            <>
              {campaigns.length === 0 &&
                (isEmpty(search)
                  ? (
                    <Box>
                      <Typography
                        color={color.grey[900]}
                        fontWeight={400}
                        fontSize={18}
                      >
                        Create your first campaign
                      </Typography>

                      <Typography
                        color={color.grey[700]}
                        fontWeight={400}
                        fontSize={16}
                      >
                        You have yet to create a campaign before, you can create one
                        here.
                      </Typography>
                      <Typography
                        color={color.grey[700]}
                        fontWeight={400}
                        fontSize={16}
                      >
                        {'If you think this is an error please'}
                        <Link
                          ml={0.5}
                          href={`mailto:${ADMIN_CONTACT_EMAIL}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Typography
                            component="span"
                            sx={{ textDecoration: 'underline' }}
                            color={color.primary[600]}
                            fontWeight={400}
                            fontSize={16}
                          >
                            create a Jira ticket
                          </Typography>
                        </Link>
                        {' and our support team will start working on a resolution.'}
                      </Typography>
                    </Box>
                  )
                  : (
                    <Box mt={3}>
                      <Typography
                        color={color.grey[900]}
                        fontWeight={500}
                        fontSize={18}
                      >
                        No results found
                      </Typography>
                    </Box>
                  ))}

              <Box
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
              >
                <Box py={3}>
                  <Grid container spacing={3}>
                    {campaigns.map((item: any) => (
                      <Grid key={item._id} item xs={12} sm={6} md={4}>
                        <CampaignListCard
                          id={item._id}
                          name={item.name}
                          htmlTemplate={item.htmlTemplate}
                          plainTextTemplate={item.plainTextTemplate}
                          smsTemplate={item.smsTemplate}
                          ivrTemplate={item.ivrTemplate}
                          initCampaigns={initCampaigns}
                        />
                      </Grid>
                    ))}

                    {campaigns.length < 9 && (
                      <Grid item xs={12} sm={6} md={4}>
                        <AddCampaignCard setActiveTab={props.setActiveTab} />
                      </Grid>
                    )}
                  </Grid>
                </Box>

                {filteredCampaigns.length > 9 && (
                  <Pagination
                    count={pageCount}
                    page={page}
                    onChange={handlePagination}
                  />
                )}
              </Box>
            </>
          )}
      </Box>
    </Box>
  )
}
